import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { 
  MdScience, 
  MdPublic, 
  MdSchool, 
  MdAccountBalance, 
  MdApps 
} from 'react-icons/md';
import { BsBarChartFill, BsInboxFill } from 'react-icons/bs';
import PageHeader from '../../components/PageHeader/PageHeader';
import Table from '../../components/Table/Table';
import FacultyTable from '../../components/FacultyTable/FacultyTable';
import Select from '../../components/Select/Select';
import FilterCard from '../../components/FilterCard/FilterCard';
import Modal from '../../components/Modal/Modal';
import './Projeler.css';

const NoDataFound = () => (
  <div className="no-data-found">
    <BsInboxFill className="no-data-icon" />
    <h3 className="no-data-title">Gösterilecek Veri Bulunamadı</h3>
    <p className="no-data-text">
      Arama kriterlerinize uygun veri bulunmamaktadır. Lütfen filtrelerinizi değiştirerek tekrar deneyin.
    </p>
  </div>
);

const Projeler = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    yil: [],
    durum: [],
    searchTerm: ''
  });
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    const loadTreeMap = async () => {
      try {
        const TreeMap = await import('highcharts/modules/treemap');
        if (typeof TreeMap === 'function') {
          TreeMap(Highcharts);
        } else if (typeof TreeMap.default === 'function') {
          TreeMap.default(Highcharts);
        } else {
          console.warn('TreeMap module loaded but initialization function not found');
        }
      } catch (error) {
        console.error('Error loading TreeMap module:', error);
      }
    };
    loadTreeMap();
  }, []);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const projectTypes = [
    { 
      title: 'TÜBİTAK',
      count: 35,
      icon: <MdScience size={36} className="project-type-icon tubitak" />,
      color: '#4CAF50'
    },
    { 
      title: 'AB',
      count: 15,
      icon: <MdPublic size={36} className="project-type-icon ab" />,
      color: '#2196F3'
    },
    { 
      title: 'BAP',
      count: 40,
      icon: <MdSchool size={36} className="project-type-icon bap" />,
      color: '#FFC107'
    },
    { 
      title: 'Döner',
      count: 20,
      icon: <MdAccountBalance size={36} className="project-type-icon doner" />,
      color: '#9C27B0'
    },
    { 
      title: 'Diğer',
      count: 15,
      icon: <MdApps size={36} className="project-type-icon other" />,
      color: '#FF5722'
    }
  ];

  const projectList = [
    {
      id: 1,
      name: "Tıp Öğretimi Hakkında",
      type: "Kitap",
      supervisor: "Prof. Dr. Selma Metintaş",
      status: "Tamamlandı",
      budget: "45.458.482 ₺"
    },
    {
      id: 2,
      name: "Kimyevi Endüstri Hakkında",
      type: "Makale",
      supervisor: "Prof. Dr. Lorem Ipsum",
      status: "Devam Ediyor",
      budget: "48.458.482 ₺"
    },
    {
      id: 3,
      name: "Tiyatro Hakkında",
      type: "Kitap",
      supervisor: "Prof. Dr. Aqua Qularis",
      status: "Tamamlandı",
      budget: "38.458.482 ₺"
    }
  ];

  const tableColumns = [
    { title: 'Proje', key: 'name', sortable: true },
    { title: 'Tür', key: 'type', sortable: true },
    { title: 'Yürütücü', key: 'supervisor', sortable: true },
    { 
      title: 'Durum', 
      key: 'status',
      sortable: true,
      render: (value) => (
        <span className={`status-badge ${value === 'Tamamlandı' ? 'completed' : 'in-progress'}`}>
          {value}
        </span>
      )
    },
    { title: 'Bütçe', key: 'budget', sortable: true }
  ];

  const getSortedData = () => {
    if (!sortConfig.key) return projectList;

    const filteredData = projectList.filter(item => 
      Object.values(item).some(val => 
        val.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    );

    if (filteredData.length === 0) {
      return [];
    }

    return [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const renderTableContent = () => {
    const sortedData = getSortedData();
    
    if (sortedData.length === 0) {
      return <NoDataFound />;
    }

    return (
      <Table 
        data={sortedData}
        columns={tableColumns}
        searchPlaceholder="Proje Ara..."
        actionButton="Proje İşlemleri"
        actionButtonVariant="primary"
        onSearch={setSearchText}
        searchValue={searchText}
        onSort={handleSort}
        sortConfig={sortConfig}
      />
    );
  };

  const chartOptions = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent'
    },
    title: {
      text: 'Proje Türlerine Göre Dağılım',
      style: { 
        fontSize: '16px',
        fontWeight: '500'
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '{point.name}: %{point.percentage:.1f}',
          distance: 20
        }
      }
    },
    series: [{
      name: 'Projeler',
      data: [
        { name: 'TÜBİTAK', y: 28.0 },
        { name: 'AB', y: 12.0 },
        { name: 'BAP', y: 32.0 },
        { name: 'Döner', y: 16.0 },
        { name: 'Diğer', y: 12.0 }
      ]
    }]
  };

  const departmentChartOptions = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent'
    },
    title: {
      text: 'Proje Sayıları / Bölüm',
      style: { 
        fontSize: '16px',
        fontWeight: '500'
      }
    },
    xAxis: {
      categories: ['Bilgisayar Müh.', 'Elektrik Müh.', 'Makine Müh.', 'İnşaat Müh.', 'Kimya Müh.'],
      labels: {
        style: {
          fontSize: '12px',
          color: '#64748b'
        }
      }
    },
    yAxis: {
      title: {
        text: 'Proje Sayısı',
        style: {
          color: '#64748b'
        }
      },
      labels: {
        style: {
          fontSize: '12px',
          color: '#64748b'
        }
      }
    },
    series: [{
      name: 'Proje Sayısı',
      data: [25, 20, 15, 18, 22],
      color: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, '#4F46E5'],
          [1, '#818CF8']
        ]
      }
    }],
    plotOptions: {
      column: {
        borderRadius: 4,
        borderWidth: 0
      }
    },
    tooltip: {
      useHTML: true,
      backgroundColor: 'rgba(255, 255, 255, 0.96)',
      borderWidth: 0,
      borderRadius: 8,
      shadow: true,
      padding: 12,
      style: {
        fontSize: '13px'
      }
    },
    credits: { enabled: false }
  };

  const budgetChartOptions = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent'
    },
    title: {
      text: 'Bütçe / Bölüm',
      style: { 
        fontSize: '16px',
        fontWeight: '500'
      }
    },
    xAxis: {
      categories: ['Bilgisayar Müh.', 'Elektrik Müh.', 'Makine Müh.', 'İnşaat Müh.', 'Kimya Müh.'],
      labels: {
        style: {
          fontSize: '12px',
          color: '#64748b'
        }
      }
    },
    yAxis: {
      title: {
        text: 'Bütçe (TL)',
        style: {
          color: '#64748b'
        }
      },
      labels: {
        style: {
          fontSize: '12px',
          color: '#64748b'
        }
      }
    },
    series: [{
      name: 'Bütçe',
      data: [1500000, 1200000, 900000, 1100000, 1300000],
      color: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, '#10B981'],
          [1, '#34D399']
        ]
      }
    }],
    plotOptions: {
      column: {
        borderRadius: 4,
        borderWidth: 0
      }
    },
    tooltip: {
      useHTML: true,
      backgroundColor: 'rgba(255, 255, 255, 0.96)',
      borderWidth: 0,
      borderRadius: 8,
      shadow: true,
      padding: 12,
      style: {
        fontSize: '13px'
      },
      formatter: function() {
        return '<div style="min-width: 150px;">' +
               '<div style="font-weight: 600; margin-bottom: 5px; color: #1e293b;">' + 
               this.x + '</div>' +
               '<div style="display: flex; justify-content: space-between; color: #64748b;">' +
               '<span>Bütçe:</span>' +
               '<span style="font-weight: 600; color: #10B981">' +
               new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(this.y) +
               '</span>' +
               '</div></div>';
      }
    },
    credits: { enabled: false }
  };

  const fakulteData = [
    {
      fakulte: "Fen-Edebiyat Fakültesi",
      bolum: "Kimya",
      projeSayisi: 142,
      butce: "45.477.683,63 ₺"
    },
    {
      fakulte: "Kimya-Metalurji Fakültesi",
      bolum: "Metalurji ve Malzeme Mühendisliği",
      projeSayisi: 100,
      butce: "49.001.045,75 ₺"
    },
    {
      fakulte: "Makina Fakültesi",
      bolum: "Makina Mühendisliği",
      projeSayisi: 88,
      butce: "69.350.221,58 ₺"
    },
    {
      fakulte: "İnşaat Fakültesi",
      bolum: "Çevre Mühendisliği",
      projeSayisi: 87,
      butce: "86.480.142,19 ₺"
    },
    {
      fakulte: "Mimarlık Fakültesi",
      bolum: "Mimarlık",
      projeSayisi: 77,
      butce: "38.300.909,13 ₺"
    }
  ];

  const yillar = [
    { value: '2024', label: '2024' },
    { value: '2023', label: '2023' },
    { value: '2022', label: '2022' },
    { value: '2021', label: '2021' }
  ];

  const durumlar = [
    { value: 'devam', label: 'Devam Eden' },
    { value: 'bitti', label: 'Tamamlandı' },
    { value: 'gelecek', label: 'Planlanan' }
  ];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const projectButtonContent = (
    <>
      <BsBarChartFill />
      <span>Görselleştir (Proje Sayısı/Bölüm-Enstitü)</span>
    </>
  );

  const budgetButtonContent = (
    <>
      <BsBarChartFill />
      <span>Görselleştir (Bütçe/Bölüm-Enstitü)</span>
    </>
  );

  const handleShowProjectsChart = (data) => {
    const treeMapData = data.map(item => ({
      name: item.bolum,
      value: item.projeSayisi,
      colorValue: item.projeSayisi
    }));

    const options = {
      chart: {
        type: 'treemap',
        height: '600px'
      },
      title: {
        text: 'Proje Sayıları / Bölüm-Enstitü',
        style: { 
          fontSize: '18px',
          fontWeight: '600',
          color: '#1e293b'
        }
      },
      series: [{
        layoutAlgorithm: 'squarified',
        data: treeMapData,
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b><br>{point.value}',
          style: {
            fontSize: '13px',
            textOutline: 'none',
            color: '#ffffff'
          }
        },
        borderWidth: 3,
        borderColor: '#ffffff',
        levels: [{
          level: 1,
          layoutAlgorithm: 'squarified',
          dataLabels: {
            enabled: true
          }
        }]
      }],
      tooltip: {
        useHTML: true,
        pointFormat: '<b>{point.name}</b><br>Proje Sayısı: {point.value}'
      },
      colorAxis: {
        minColor: '#FFFFFF',
        maxColor: '#4F46E5'
      }
    };

    setChartData(options);
    setShowProjectsModal(true);
  };

  const handleShowBudgetChart = (data) => {
    const treeMapData = data.map(item => ({
      name: item.bolum,
      value: parseFloat(item.butce?.replace(/[^0-9.-]+/g,"")) || 0,
      colorValue: parseFloat(item.butce?.replace(/[^0-9.-]+/g,"")) || 0
    }));

    const options = {
      chart: {
        type: 'treemap',
        height: '600px'
      },
      title: {
        text: 'Bütçe / Bölüm-Enstitü',
        style: { 
          fontSize: '18px',
          fontWeight: '600',
          color: '#1e293b'
        }
      },
      series: [{
        layoutAlgorithm: 'squarified',
        data: treeMapData,
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b><br>{point.value:,.0f} ₺',
          style: {
            fontSize: '13px',
            textOutline: 'none',
            color: '#ffffff'
          }
        },
        borderWidth: 3,
        borderColor: '#ffffff',
        levels: [{
          level: 1,
          layoutAlgorithm: 'squarified',
          dataLabels: {
            enabled: true
          }
        }]
      }],
      tooltip: {
        useHTML: true,
        pointFormatter: function() {
          return '<b>' + this.name + '</b><br>Bütçe: ' + 
            Highcharts.numberFormat(this.value, 0, ',', '.') + ' ₺';
        }
      },
      colorAxis: {
        minColor: '#FFFFFF',
        maxColor: '#10B981'
      }
    };

    setChartData(options);
    setShowBudgetModal(true);
  };

  const renderFacultyTableContent = () => {
    const filteredData = fakulteData.filter(item =>
      Object.values(item).some(val =>
        val.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    );

    if (filteredData.length === 0) {
      return <NoDataFound />;
    }

    return (
      <FacultyTable 
        data={filteredData} 
        onShowProjectsChart={handleShowProjectsChart}
        onShowBudgetChart={handleShowBudgetChart}
        projectButtonContent={projectButtonContent}
        budgetButtonContent={budgetButtonContent}
      />
    );
  };

  return (
    <div className="projects-container container-fluid">
      <PageHeader 
        title="Projeler"
        description="Proje istatistikleri ve detaylı bilgiler"
        breadcrumbs={[
          { text: 'Ana Sayfa', href: '/' },
          { text: 'Projeler'},
        ]}
      />
      
      <div className="row g-3 mb-4">
        {projectTypes.map((type) => (
          <div key={type.title} className="col-6 col-md-4 col-lg">
            <div className="project-type-card" style={{ borderTop: `3px solid ${type.color}` }}>
              {type.icon}
              <div className="project-type-info">
                <h3>{type.title}</h3>
                <span className="project-count">{type.count}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12">
          <div className="stats-card">
            <div className="stats-row">
              <div className="stats-item">
                <span className="stats-label">Öğretim Üyesi Sayısı</span>
                <span className="stats-value">1230</span>
              </div>
              <div className="stats-item">
                <span className="stats-label">Toplam Proje Sayısı (2024)</span>
                <span className="stats-value">1597</span>
                <span className="stats-budget">1.346.768.566,90 ₺</span>
              </div>
              <div className="stats-item">
                <span className="stats-label">Toplam Proje Sayısı (2019-2024)</span>
                <span className="stats-value">5158</span>
                <span className="stats-budget">2.309.555.464,07 ₺</span>
              </div>
            </div>
            <div className="stats-row">
              <div className="stats-item">
                <span className="stats-label">TÜBİTAK (2024)</span>
                <span className="stats-value">181</span>
                <span className="stats-budget">236.137.006,70 ₺</span>
              </div>
              <div className="stats-item">
                <span className="stats-label">AB (2024)</span>
                <span className="stats-value">43</span>
                <span className="stats-budget">260.408.394,74 ₺</span>
              </div>
              <div className="stats-item">
                <span className="stats-label">BAP (2024)</span>
                <span className="stats-value">1118</span>
                <span className="stats-budget">169.813.257,92 ₺</span>
              </div>
              <div className="stats-item">
                <span className="stats-label">TTO (2024)</span>
                <span className="stats-value">220</span>
                <span className="stats-budget">591.178.044,54 ₺</span>
              </div>
              <div className="stats-item">
                <span className="stats-label">Döner Sermaye (2024)</span>
                <span className="stats-value">32</span>
                <span className="stats-budget">88.203.463,00 ₺</span>
              </div>
              <div className="stats-item">
                <span className="stats-label">Diğer (2024)</span>
                <span className="stats-value">3</span>
                <span className="stats-budget">1.025.400,00 ₺</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12">
          {renderFacultyTableContent()}
        </div>
      </div>

      <div className="row g-3 mb-4 inner-card" style={{ zIndex: 10000, position: 'relative' }}>
        <div className="col-12">
          <div className="inner-card-container">
            <div className="card-header">
              <h3>Proje Ara</h3>
            </div>
            <FilterCard>
              <FilterCard.Row>
                <FilterCard.Col>
                  <label htmlFor="year-select" className="selectLabel">Yıl</label>
                  <Select
                    id="year-select"
                    value={filters.yil}
                    onChange={(value) => handleFilterChange('yil', value)}
                    options={yillar}
                    placeholder="Yıl seçiniz"
                    aria-label="Yıl seçiniz"
                  />
                </FilterCard.Col>
                <FilterCard.Col>
                  <label htmlFor="status-select" className="selectLabel">Durum</label>
                  <Select
                    id="status-select"
                    value={filters.durum}
                    onChange={(value) => handleFilterChange('durum', value)}
                    options={durumlar}
                    placeholder="Durum seçiniz"
                    aria-label="Durum seçiniz"
                  />
                </FilterCard.Col>
                <FilterCard.Col>
                  <FilterCard.Input
                    label="Proje Ara"
                    type="text"
                    placeholder="Proje adı ile ara..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    aria-label="Proje adı ile ara"
                  />
                </FilterCard.Col>
              </FilterCard.Row>
            </FilterCard>
            <div className="col-12">
              {renderTableContent()}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-6">
          <div className="chart-section">
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
            />
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="chart-section">
            <HighchartsReact
              highcharts={Highcharts}
              options={departmentChartOptions}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="chart-section">
            <HighchartsReact
              highcharts={Highcharts}
              options={budgetChartOptions}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={showProjectsModal}
        onClose={() => setShowProjectsModal(false)}
        title="Proje Sayıları / Bölüm-Enstitü"
      >
        <div className="chart-container">
          <HighchartsReact
            highcharts={Highcharts}
            options={chartData}
          />
        </div>
      </Modal>

      <Modal
        isOpen={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        title="Bütçe / Bölüm-Enstitü"
      >
        <div className="chart-container">
          <HighchartsReact
            highcharts={Highcharts}
            options={chartData}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Projeler; 